/**
 * Shared client helpers for hcm-user-security-access-map (User Lookup + Sql Studio row cap).
 */
import axios from 'axios';
import { getApiBase } from '../apiBase';

export const SECURITY_ACCESS_MAP_CORE_RECIPE_ID = 'hcm-user-security-access-map-core';
export const SECURITY_ACCESS_MAP_RECIPE_ID = 'hcm-user-security-access-map';
export const FUSION_USERS_DIRECTORY_RECIPE_ID = 'hcm-fusion-users-directory';
export const SECURITY_ACCESS_MAP_CORE_ROW_LIMIT = 2000;
export const SECURITY_ACCESS_MAP_ROW_LIMIT = 5000;
export const FUSION_USERS_DIRECTORY_ROW_LIMIT = 15000;

const API = axios.create({
  baseURL: getApiBase(),
  withCredentials: true,
  timeout: 240000,
});

export function formatApiError(value, fallback = 'Request failed') {
  if (value == null || value === '') return fallback;
  if (typeof value === 'string') return value;
  if (typeof value === 'object') {
    const msg = value.message || value.error || value.msg;
    const code = value.oraCode || value.code;
    if (msg && code) return `${code}: ${msg}`;
    if (msg) return String(msg);
    try {
      return JSON.stringify(value);
    } catch {
      return fallback;
    }
  }
  return String(value);
}

/** Load pod-tested SQL from services/copilot/sqlLibrary/{recipeId}.full.sql */
export async function fetchSecurityAccessMapSql(
  recipeId = SECURITY_ACCESS_MAP_CORE_RECIPE_ID
) {
  const id = String(recipeId || SECURITY_ACCESS_MAP_CORE_RECIPE_ID).trim();
  const res = await API.get(`/api/copilot/sql-library/${id}`, {
    validateStatus: () => true,
  });
  if (res.status !== 200 || !res.data?.sql) {
    throw new Error(res.data?.error || `Could not load security access map SQL (HTTP ${res.status})`);
  }
  return res.data.sql;
}

/** Execute the access-map query via BIP AI SQL Runner. */
export async function runSecurityAccessMapSql(sql, { rowLimit } = {}) {
  const limit =
    rowLimit ??
    (String(sql || '').includes('FETCH FIRST 2000') ? SECURITY_ACCESS_MAP_CORE_ROW_LIMIT : SECURITY_ACCESS_MAP_ROW_LIMIT);
  const res = await API.post(
    '/api/copilot/run-oracle-sql',
    { sql, rowLimit: limit },
    { validateStatus: () => true }
  );
  const data = res.data || {};
  if (res.status !== 200) {
    throw new Error(formatApiError(data.error, `HTTP ${res.status}`));
  }
  if (!data.ok) {
    throw new Error(formatApiError(data.error, 'Query failed'));
  }
  return {
    rows: data.rows || [],
    columns: data.columns || [],
    elapsedMs: data.elapsedMs,
    rowCount: data.rowCount,
  };
}

/** Inject per-user filter on each branch (BIP has no bind params in this path). */
export function injectSecurityAccessMapUsernameFilter(sql, username) {
  const u = String(username || '').trim();
  if (!u) return sql;
  const esc = u.replace(/'/g, "''");
  return sql.replace(
    /WHERE NVL\(pu\.suspended/gi,
    `WHERE pu.username = '${esc}' AND NVL(pu.suspended`
  );
}

function normRowKey(row, ...names) {
  if (!row || typeof row !== 'object') return '';
  const keys = Object.keys(row);
  for (const want of names) {
    const hit = keys.find((k) => k.toLowerCase() === want.toLowerCase());
    if (hit != null && row[hit] != null && String(row[hit]).trim() !== '') {
      return String(row[hit]).trim();
    }
  }
  return '';
}

function roleLabelSet(rows, username) {
  const labels = new Set();
  for (const row of rows || []) {
    if (username && normRowKey(row, 'username').toLowerCase() !== username.toLowerCase()) continue;
    const name = normRowKey(row, 'access_name', 'role_name').toLowerCase();
    if (name) labels.add(name);
    const detail = normRowKey(row, 'access_detail', 'description').toLowerCase();
    if (detail) {
      const codeMatch = detail.match(/privilege bundle code:\s*([^\s.]+)/i);
      if (codeMatch?.[1]) labels.add(codeMatch[1].toLowerCase());
    }
  }
  return labels;
}

function scimRoleLabels(role) {
  const labels = new Set();
  if (role?.roleCode) labels.add(String(role.roleCode).trim().toLowerCase());
  if (role?.displayName) labels.add(String(role.displayName).trim().toLowerCase());
  return labels;
}

function scimRoleAlreadyInMap(role, labels) {
  for (const l of scimRoleLabels(role)) {
    if (labels.has(l)) return true;
  }
  return false;
}

function metadataCategory(meta) {
  if (normRowKey(meta, 'job_role').toUpperCase() === 'Y') return 'INHERITED_JOB';
  if (normRowKey(meta, 'duty_role').toUpperCase() === 'Y') return 'INHERITED_DUTY';
  if (normRowKey(meta, 'data_role').toUpperCase() === 'Y') return 'INHERITED_DATA';
  if (normRowKey(meta, 'abstract_role').toUpperCase() === 'Y') return 'ABSTRACT_ROLE';
  return 'EFFECTIVE_SCIM';
}

/** Resolve role flags from PER_ROLES_DN_VL for SCIM role codes not returned by the map SQL. */
export async function fetchRoleMetadataByCodes(roleCodes) {
  const codes = [...new Set((roleCodes || []).map((c) => String(c || '').trim()).filter(Boolean))];
  if (!codes.length) return [];
  const esc = (s) => String(s).replace(/'/g, "''");
  const inList = codes
    .slice(0, 200)
    .map((c) => `'${esc(c.toUpperCase())}'`)
    .join(', ');
  const sql = `SELECT prdn.role_common_name,
       prdn.role_name,
       prdn.description,
       prdn.job_role,
       prdn.duty_role,
       prdn.data_role,
       prdn.abstract_role
  FROM fusion.per_roles_dn_vl prdn
 WHERE UPPER(TRIM(prdn.role_common_name)) IN (${inList})
    OR UPPER(TRIM(prdn.role_name)) IN (${inList})
 FETCH FIRST 500 ROWS ONLY`;
  const result = await runSecurityAccessMapSql(sql);
  return result.rows || [];
}

/**
 * Merge SCIM effective roles into map rows when SQL hierarchy expansion missed them
 * (no PER_SRM rows or LDAP-only hierarchy on pod).
 */
export function mergeScimRolesIntoAccessMapRows(rows, scimRoles, { username = '', displayName = '', personNumber = '' } = {}) {
  if (!Array.isArray(rows) || !Array.isArray(scimRoles) || !scimRoles.length) return rows || [];
  const u = String(username || '').trim();
  const labels = roleLabelSet(rows, u);
  const missing = scimRoles.filter((r) => !scimRoleAlreadyInMap(r, labels));
  if (!missing.length) return rows;

  const base = {
    username: u || normRowKey(rows[0], 'username'),
    display_name: displayName,
    person_number: personNumber,
    active_flag: 'Y',
  };

  const extras = [];
  for (const role of missing) {
    const accessName = role.displayName || role.roleCode;
    extras.push({
      ...base,
      access_layer: 'ASSIGNED_ROLE',
      access_category: 'EFFECTIVE_SCIM',
      access_name: accessName,
      access_detail:
        `Effective role from SCIM (includes hierarchy); not a direct PER_USER_ROLES row. Code: ${role.roleCode}.` +
        (role.description ? ` ${role.description}` : ''),
    });
    labels.add(String(role.roleCode || '').toLowerCase());
    labels.add(String(accessName || '').toLowerCase());
  }
  return [...(rows || []), ...extras];
}

/** Apply SCIM merge after optional metadata lookup (duty privileges). */
export async function enrichAccessMapWithScimRoles(rows, scimRoles, userMeta = {}) {
  let merged = mergeScimRolesIntoAccessMapRows(rows, scimRoles, userMeta);
  const u = String(userMeta.username || '').trim();
  const labels = roleLabelSet(merged, u);
  const missing = (scimRoles || []).filter((r) => !scimRoleAlreadyInMap(r, labels));
  if (!missing.length) return merged;

  let metadata = [];
  try {
    metadata = await fetchRoleMetadataByCodes(missing.map((r) => r.roleCode).filter(Boolean));
  } catch {
    return merged;
  }

  const base = {
    username: u || normRowKey(merged[0], 'username'),
    display_name: userMeta.displayName || '',
    person_number: userMeta.personNumber || '',
    active_flag: 'Y',
  };

  for (const meta of metadata) {
    const roleName = normRowKey(meta, 'role_name') || normRowKey(meta, 'role_common_name');
    const roleCode = normRowKey(meta, 'role_common_name');
    if (!roleName || labels.has(roleName.toLowerCase()) || (roleCode && labels.has(roleCode.toLowerCase()))) {
      continue;
    }
    const category = metadataCategory(meta);
    merged = [
      ...merged,
      {
        ...base,
        access_layer: 'ASSIGNED_ROLE',
        access_category: category,
        access_name: roleName,
        access_detail:
          `Effective role from SCIM + PER_ROLES_DN_VL flags. Code: ${roleCode || roleName}. ` +
          (normRowKey(meta, 'description') || ''),
      },
    ];
    labels.add(roleName.toLowerCase());
    if (roleCode) labels.add(roleCode.toLowerCase());

    if (normRowKey(meta, 'duty_role').toUpperCase() === 'Y') {
      merged = [
        ...merged,
        {
          ...base,
          access_layer: 'PRIVILEGE',
          access_category: 'INHERITED_DUTY',
          access_name: roleName,
          access_detail:
            `Privilege from SCIM effective duty role (${roleCode || roleName}). ` +
            (normRowKey(meta, 'description') || 'Duty role — menu grants need Security Console.'),
        },
      ];
    }
  }
  return merged;
}

/** Load library SQL and run — used by User Lookup. Defaults to core (BIP-safe) recipe. */
export async function loadAndRunSecurityAccessMap({
  username,
  scimRoles = null,
  userMeta = {},
  recipeId = SECURITY_ACCESS_MAP_CORE_RECIPE_ID,
} = {}) {
  const id = String(recipeId || SECURITY_ACCESS_MAP_CORE_RECIPE_ID).trim();
  const rowLimit =
    id === SECURITY_ACCESS_MAP_CORE_RECIPE_ID
      ? SECURITY_ACCESS_MAP_CORE_ROW_LIMIT
      : SECURITY_ACCESS_MAP_ROW_LIMIT;
  let sql = await fetchSecurityAccessMapSql(id);
  if (username) sql = injectSecurityAccessMapUsernameFilter(sql, username);
  const result = await runSecurityAccessMapSql(sql, { rowLimit });
  if (Array.isArray(scimRoles) && scimRoles.length) {
    result.rows = await enrichAccessMapWithScimRoles(result.rows, scimRoles, {
      username,
      displayName: userMeta.displayName,
      personNumber: userMeta.personNumber,
    });
  }
  return result;
}

/** Load pod-tested directory SQL (one row per active Fusion user). */
export async function fetchFusionUsersDirectorySql() {
  const res = await API.get(`/api/copilot/sql-library/${FUSION_USERS_DIRECTORY_RECIPE_ID}`, {
    validateStatus: () => true,
  });
  if (res.status !== 200 || !res.data?.sql) {
    throw new Error(res.data?.error || `Could not load Fusion users directory SQL (HTTP ${res.status})`);
  }
  return res.data.sql;
}

export async function runFusionUsersDirectorySql(sql) {
  const res = await API.post(
    '/api/copilot/run-oracle-sql',
    { sql, rowLimit: FUSION_USERS_DIRECTORY_ROW_LIMIT },
    { validateStatus: () => true }
  );
  const data = res.data || {};
  if (res.status !== 200) {
    throw new Error(formatApiError(data.error, `HTTP ${res.status}`));
  }
  if (!data.ok) {
    throw new Error(formatApiError(data.error, 'Directory query failed'));
  }
  return {
    rows: data.rows || [],
    elapsedMs: data.elapsedMs,
    rowCount: data.rowCount,
  };
}

function normDirectoryKey(row, ...names) {
  if (!row || typeof row !== 'object') return '';
  const keys = Object.keys(row);
  for (const want of names) {
    const hit = keys.find((k) => k.toLowerCase() === want.toLowerCase());
    if (hit != null && row[hit] != null && String(row[hit]).trim() !== '') {
      return String(row[hit]).trim();
    }
  }
  return '';
}

/** Map directory query rows to combobox entries (username, displayName, personNumber). */
export function directoryRowsToPickerUsers(rows) {
  const byUsername = new Map();
  for (const row of rows || []) {
    const username = normDirectoryKey(row, 'username', 'user_name');
    if (!username) continue;
    const existing = byUsername.get(username);
    const entry = {
      username,
      displayName: normDirectoryKey(row, 'display_name', 'employee_name', 'full_name'),
      personNumber: normDirectoryKey(row, 'person_number', 'employee_number'),
    };
    if (!existing) {
      byUsername.set(username, entry);
      continue;
    }
    if (!existing.displayName && entry.displayName) existing.displayName = entry.displayName;
    if (!existing.personNumber && entry.personNumber) existing.personNumber = entry.personNumber;
  }
  return [...byUsername.values()].sort((a, b) => {
    const aLabel = (a.displayName || a.username).toLowerCase();
    const bLabel = (b.displayName || b.username).toLowerCase();
    return aLabel.localeCompare(bLabel);
  });
}

export async function loadFusionUsersDirectory() {
  const sql = await fetchFusionUsersDirectorySql();
  const result = await runFusionUsersDirectorySql(sql);
  return {
    users: directoryRowsToPickerUsers(result.rows),
    elapsedMs: result.elapsedMs,
    rowCount: result.rowCount,
  };
}

/** Server-side A/B diff (loads BIP maps) or client rows when rowsA/rowsB provided. */
export async function compareSecurityUsers({
  usernameA,
  usernameB,
  useExtended = false,
  rowsA = null,
  rowsB = null,
} = {}) {
  const res = await API.post(
    '/api/copilot/security/compare-users',
    {
      usernameA,
      usernameB,
      useExtended,
      ...(Array.isArray(rowsA) && Array.isArray(rowsB) ? { rowsA, rowsB } : {}),
    },
    { validateStatus: () => true }
  );
  const data = res.data || {};
  if (res.status !== 200 || !data.ok) {
    throw new Error(formatApiError(data.error, `Compare failed (HTTP ${res.status})`));
  }
  return data;
}

/** AI security audit — risk score + flagged roles (optional pre-loaded map rows / HR context). */
export async function auditUserRisk({
  username,
  mapRows = null,
  userData = null,
  useExtended = false,
} = {}) {
  const res = await API.post(
    '/api/copilot/audit-user-risk',
    {
      username,
      useExtended,
      ...(Array.isArray(mapRows) && mapRows.length ? { mapRows } : {}),
      ...(userData && typeof userData === 'object' ? { userData } : {}),
    },
    { validateStatus: () => true, timeout: 300000 }
  );
  const data = res.data || {};
  if (res.status !== 200) {
    throw new Error(formatApiError(data.error, `Security audit failed (HTTP ${res.status})`));
  }
  return data;
}

/** @deprecated Use auditUserRisk — kept as alias for older callers. */
export async function analyzeEntitlements(opts = {}) {
  return auditUserRisk(opts);
}

/** Risk badge tier from score (1–100). */
export function riskScoreTier(score) {
  const n = Number(score);
  if (!Number.isFinite(n)) return 'unknown';
  if (n < 40) return 'low';
  if (n < 70) return 'medium';
  return 'high';
}

/** Client-side diff when both maps are already loaded (no extra BIP). */
export function diffAccessMapsClient(rowsA, rowsB) {
  const key = (row) => {
    const layer = normRowKey(row, 'access_layer', 'layer').toUpperCase();
    const category = normRowKey(row, 'access_category', 'category').toUpperCase();
    const name = normRowKey(row, 'access_name', 'role_name', 'name').toLowerCase();
    return `${layer}|${category}|${name}`;
  };
  const toItem = (row) => ({
    key: key(row),
    layer: normRowKey(row, 'access_layer', 'layer').toUpperCase(),
    category: normRowKey(row, 'access_category', 'category'),
    name: normRowKey(row, 'access_name', 'role_name', 'name'),
    detail: normRowKey(row, 'access_detail', 'description', 'detail'),
  });
  const diffLayer = (layer, exclude = []) => {
    const ex = new Set(exclude.map((c) => c.toUpperCase()));
    const a = (rowsA || [])
      .map(toItem)
      .filter((i) => i.layer === layer && i.name && !ex.has(String(i.category || '').toUpperCase()));
    const b = (rowsB || [])
      .map(toItem)
      .filter((i) => i.layer === layer && i.name && !ex.has(String(i.category || '').toUpperCase()));
    const mapA = new Map(a.map((i) => [i.key, i]));
    const mapB = new Map(b.map((i) => [i.key, i]));
    const onlyA = [];
    const onlyB = [];
    const both = [];
    for (const [k, item] of mapA) {
      if (mapB.has(k)) both.push(item);
      else onlyA.push(item);
    }
    for (const [k, item] of mapB) {
      if (!mapA.has(k)) onlyB.push(item);
    }
    const sort = (x, y) => (x.name || '').localeCompare(y.name || '');
    return {
      onlyA: onlyA.sort(sort),
      onlyB: onlyB.sort(sort),
      both: both.sort(sort),
    };
  };
  const roles = diffLayer('ASSIGNED_ROLE', ['NO_ROLES']);
  const priv = diffLayer('PRIVILEGE');
  const data = diffLayer('DATA_SECURITY');
  const person = diffLayer('PERSON_SCOPE');
  return {
    rolesOnlyA: roles.onlyA,
    rolesOnlyB: roles.onlyB,
    rolesBoth: roles.both,
    privilegesOnlyA: priv.onlyA,
    privilegesOnlyB: priv.onlyB,
    privilegesBoth: priv.both,
    dataSecurityOnlyA: data.onlyA,
    dataSecurityOnlyB: data.onlyB,
    dataSecurityBoth: data.both,
    personScopeOnlyA: person.onlyA,
    personScopeOnlyB: person.onlyB,
    personScopeBoth: person.both,
  };
}

/** GET /api/security/sod-check/:username — evaluate SoD rules for a Fusion username. */
export async function checkUserSoD({ username, connectionId } = {}) {
  const u = String(username || '').trim();
  if (!u) throw new Error('username is required');
  const params = connectionId ? { connectionId } : undefined;
  const res = await API.get(`/api/security/sod-check/${encodeURIComponent(u)}`, {
    params,
    validateStatus: () => true,
  });
  const data = res.data || {};
  if (res.status !== 200) {
    throw new Error(formatApiError(data.error, `SoD check failed (HTTP ${res.status})`));
  }
  return data;
}

export async function fetchSodRules() {
  const res = await API.get('/api/copilot/sod/rules', { validateStatus: () => true });
  const data = res.data || {};
  if (res.status !== 200) {
    throw new Error(formatApiError(data.error, `Failed to load SoD rules (HTTP ${res.status})`));
  }
  return data.rules || [];
}

export async function createSodRule(payload) {
  const res = await API.post('/api/copilot/sod/rules', payload, { validateStatus: () => true });
  const data = res.data || {};
  if (res.status !== 201 && res.status !== 200) {
    throw new Error(formatApiError(data.error, `Failed to create SoD rule (HTTP ${res.status})`));
  }
  return data.rule;
}

export async function updateSodRule(id, payload) {
  const res = await API.put(`/api/copilot/sod/rules/${id}`, payload, { validateStatus: () => true });
  const data = res.data || {};
  if (res.status !== 200) {
    throw new Error(formatApiError(data.error, `Failed to update SoD rule (HTTP ${res.status})`));
  }
  return data.rule;
}

export async function deleteSodRule(id) {
  const res = await API.delete(`/api/copilot/sod/rules/${id}`, { validateStatus: () => true });
  if (res.status !== 200) {
    throw new Error(formatApiError(res.data?.error, `Failed to delete SoD rule (HTTP ${res.status})`));
  }
}
